<template>
  <Stack gap="2">
    <TextInput
      :key="searchQueryKey"
      v-model="searchQuery"
      placeholder="Search CRM contacts..."
      @input="handleSearch"
    />

    <div
      v-if="isFetching"
      class="loading-zone"
    >
      <LoaderWithText />
    </div>

    <ul
      v-else-if="filteredContacts.length"
      class="contact-list"
    >
      <li
        v-for="contact in filteredContacts"
        :key="contact.id"
        class="contact-item"
        :class="{
          'is-selected': contact.id === selectedContactId
        }"
        @click="selectContact(contact)"
      >
        <div class="contact-name">
          {{ getContactName(contact) }}
        </div>
        <div
          v-if="contact.type"
          class="contact-type"
        >
          {{ contact.type }}
        </div>
      </li>
    </ul>

    <div
      v-else
      class="loading-zone"
    >
      <p>
        No contact found
      </p>
      <Button
        size="3"
        @click="handleReset"
      >
        Reset search
      </Button>
    </div>
  </Stack>
</template>

<script>
import { Button, LoaderWithText, Stack, TextInput } from '@stakeui';

const MAX_COUNT = 10;

export default {
  components: {
    Button,
    LoaderWithText,
    Stack,
    TextInput,
  },
  props: {
    contacts: {
      type: Array,
      required: true,
    },
    crmType: {
      type: String,
      required: true,
      validator: value => ['Wealthbox', 'Redtail', 'Salesforce'].includes(value),
    },
    isFetching: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      searchQueryKey: 0,
      selectedContactId: '', // For internal UI
      filteredContacts: [],
    };
  },
  watch: {
    contacts: {
      immediate: true,
      handler(newContacts) {
        console.log('contacts watcher triggered, newContacts:', newContacts);
        this.filterContacts();
      },
    },
  },
  created() {
    this.handleSearch();
  },
  methods: {
    async handleSearch() {
      // console.log('handleSearch called, crmType:', this.crmType, 'query:', this.searchQuery);
      if (['Redtail', 'Salesforce'].includes(this.crmType)) {
        this.$emit('search', this.searchQuery);
      } else {
        this.filterContacts();
      }
    },
    async handleReset() {
      this.searchQuery = '';
      this.searchQueryKey++; // To ensure the text input re-renders
      await this.handleSearch();
    },
    filterContacts() {
      console.log('filterContacts called, raw contacts:', this.contacts);

      const contactsToFilter = this.contacts;

      console.log('contactsToFilter:', contactsToFilter);

      if (this.searchQuery.length === 0) {
        this.filteredContacts = contactsToFilter.slice(0, MAX_COUNT);
      } else {
        const lowercaseQuery = this.searchQuery.toLowerCase();
        this.filteredContacts = contactsToFilter
            .filter(contact => this.getContactName(contact).toLowerCase().includes(lowercaseQuery))
            .slice(0, 10);
      }
      console.log('filteredContacts result:', this.filteredContacts);
    },
    selectContact(contact) {
      if (!contact || !contact.id) {
        return;
      }

      if (this.selectedContactId === contact.id) {
        this.selectedContactId = '';
        this.$emit('contact-selected', {});
        return;
      }

      console.log('selectContact called for contact:', contact);
      this.$emit('contact-selected', contact);
      this.selectedContactId = contact.id;
    },
    getContactName(contact) {
      // console.log('getContactName called for contact:', contact);
      if (this.crmType === 'Wealthbox') {
        return contact.first_name || contact.last_name ? `${contact.first_name} ${contact.last_name}` : contact.name || 'Unnamed Contact';
      } else if (this.crmType === 'Redtail') {
        return contact.full_name || `${contact.first_name} ${contact.last_name}` || contact.name || 'Unnamed Contact';
      } else if (this.crmType === 'Salesforce') {
        return contact.name || 'Unnamed Contact';
      }
      return 'Unknown Contact';
    },
  },
};
</script>

<style scoped>
.loading-zone,
.contact-list {
  border: 1px solid var(--stk-border);
  border-radius: 0.75em;
  height: 15.5em;
  padding: 0.25em;
  overflow-x: hidden;
}

.loading-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--stk-background);
  flex-direction: column;
  gap: 0.5em;
}

.contact-list {
  overflow-y: scroll;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.contact-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1em;
  padding: 0.25em 0.5em;
  border-radius: 0.5em;
  transition-duration: var(--stk-transition-duration);
  cursor: pointer;
  transition-property: background-color, color, border-color, opacity, outline-color;

  &:hover {
    background-color: var(--stk-primary-90);
    color: var(--stk-primary-40);
  }

  &.is-selected {
    background-color: var(--stk-primary-40);
    color: var(--stk-primary-100);
  }
}

.contact-type {
  font-size: 0.875em;
  opacity: 0.5;
}
</style>
